/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    CategoryHierarchy as CategoryHierarchyData,
    IProductRefinerHierarchy,
    IProductsDimensionsAvailabilities
} from '@msdyn365-commerce/commerce-entities';
import MsDyn365, { getAsset, getCatalogId, ICoreContext, getUrlSync, getParameterizeFeatureFlags, Image, IImageSettings } from '@msdyn365-commerce/core';
import {
    ChannelInventoryConfiguration,
    ProductPrice,
    ProductRefinerValue,
    ProductsDataActions,
    SortColumn,
    StoreOperationsDataActions,
    TextValueTranslation,
    SimpleProduct,
    UnitOfMeasureConversion,
    format,
    AttributeDataType,
    ProductRefinerSource,
    ProductList
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    DimensionAvailabilitiesForProductSearchResultInput,
    DimensionTypes,
    DimensionSwatchDisplayTypes,
    getDimensionAvailabilitiesForProductSearchResultAction,
    IDimensionsApp,
    ObjectExtensions,
    getCategoryUrl,
    getCategorySlug,
    OrderTemplate,
    QueryResultSettingsProxy,
    GetOrderTemplatesInput,
    getOrderTemplates,
    DeliveryMode,
    IProductInventoryInformation,
    mapAggregatedProductInventoryInformation,
    createInventoryAvailabilitySearchCriteria
} from '@msdyn365-commerce-modules/retail-actions';
import { ICategoryMap } from '@msdyn365-commerce-modules/retail-actions/dist/types/get-categories-hierarchy';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import {
    getTelemetryObject,
    ILabeledDropdownOnChangeNotification,
    ILabeledDropdownOption,
    IModuleProps,
    INodeProps,
    isMobile,
    ITelemetryContent,
    LabeledDropdown,
    UncontrolledPagination,
    VariantType,
    Tooltip
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, observable, reaction, transaction, action, when, comparer, toJS, spy } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ProductListViewMode, IProductListViewModeProps } from '../../components/product-list-component/components/product-list-view-mode';

import {
    buildListPageUrl,
    getCollectionProducts,
    getCollectionRefinersAction,
    getCurrentUrl,
    GetFullProductsByCollectionInput,
    parseQueryParam,
    ProductListInventoryFilteringOptions,
    RefinersByCollectionInput,
    sortOptions
} from './actions';
import { IFullProductsSearchResultsWithCount } from './actions/get-full-products-by-collection';
import { hydrateRefinersFromUrl } from './actions/url-utils';
import {
    ErrorMessage,
    FeatureSearchResultItem,
    getUpdatedRefinementCriteria,
    IChoiceSummaryClickNotification,
    IRefineItemCommonProps,
    IRefineItemToggleNotification,
    ISearchResultModalViewProps,
    isMatchingRefinementCriterion,
    ModalToggle,
    ProductSearchResultItems,
    SearchResultModal
} from './components';
import ChoiceSummary from './components/choice-summary';
import RefineSubmenu from './components/refine-submenu';
import { ListPageState } from './defa-search-result-container.list-page-state';
import { IDefaSearchResultContainerData } from './defa-search-result-container.data';
import { IDefaSearchResultContainerProps, preferredViewMode } from './defa-search-result-container.props.autogenerated';
import {
    ProductListComponent,
    ProductListProduct,
    productSearchResults2ProductList
} from '../../components/product-list-component/product-list-component';
import { GetCookie, SetCookie } from '../../shared-utils/cookie-utilities';
import { getActivePricesAsync, getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getDiscountBadge } from '../../shared-utils/badge-utils';
import { ProductSearchResultExt } from '../../shared-utils/types';
import ProductListCompactComponent from '../../components/product-list-compact-component/product-list-compact-component';
import { hasAgreementProducts, getAgreementProductRefinersToHide } from '../../shared-utils/agreement-product-utils';
import { getProductListsByProductIdsAsync, IProductListByProductId } from '../../actions/get-product-lists-by-product-ids';
import RefineCategoryTree from './components/refine-category-tree';
import * as GTM from '../../shared-utils/google-tag-manager/gtm';
import { LocalDropdown } from '../../components/dropdown';
import { FindEngineHeater, IFindEngineHeaderSearchParams } from './components/find-engine-heater';
import { searchVehiclesByRegIdAsync } from '../defa-find-engine-heater/actions/search-vehicles-by-reg-id';
import { ISearchResultProduct, ISearchVehiclesResponse } from '../defa-find-engine-heater/actions/search-vehicles';
import { IAppSettingsInContext } from '../../settings/app.settings.autogenerated';
import { groupSearchVehiclesResults, IFindEngineHeaterGroupedResults } from './defa-search-result-container.utils';

export interface ISearchResultContainerViewProps extends IDefaSearchResultContainerProps<IDefaSearchResultContainerData> {
    header?: React.ReactNode;
    searching?: boolean;
    products?: React.ReactNode;
    totalProductCount?: React.ReactNode;
    className?: string;
    SearchResultContainer: IModuleProps;
    pagination?: React.ReactNode;
    ProductsContainer: INodeProps;
    ProductSectionContainer: INodeProps;
    refineMenu: IRefineMenuViewProps;
    sortByOptions: ISortByViewProps;
    choiceSummary?: React.ReactNode;
    modalToggle: React.ReactNode;
    searchResultModal: ISearchResultModalViewProps;
    isMobile: boolean;
    RefineAndProductSectionContainer: INodeProps;
    searchBar?: React.ReactNode;
    errorMessage: React.ReactNode;
    FeatureSearchContainer: INodeProps;
    similarLookProduct?: React.ReactNode;
    productPrices?: ProductPrice[];
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    viewModesComponent: IProductListViewModeProps | undefined;
    selectedProductsActions?: React.ReactNode;
    productInventoryStatus?: IProductInventoryInformation[];
    simpleProducts: SimpleProduct[];
    unitConversions: UnitOfMeasureConversion[];
    onUpdateOrderTemplate: (product?: ProductSearchResultExt, newOrderTemplate?: ProductList) => Promise<OrderTemplate[]>;
    retrieveOrderTemplates: () => Promise<OrderTemplate[]>;
    categoryOverview: React.ReactNode;
    engineHeaterSearch: React.ReactNode;
    engineHeaterCurrentSearch: React.ReactNode;
    shouldShowAvailability: boolean;
}

export interface IRefineMenuViewProps {
    RefineMenuContainer: INodeProps;
    RefinerSectionContainer: INodeProps;
    refiners?: React.ReactNode[];
}

export interface ICategoryHierarchyViewProps {
    categoryHierarchyList?: React.ReactNode[];
    categoryHierarchySeparator?: React.ReactNode;
    CategoryHierarchyContainer: INodeProps;
}

export interface ISortByViewProps {
    SortingContainer: INodeProps;
    sortByDropDown?: React.ReactNode;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Title component for search result container.
 */
export interface ISearchResultTitle {
    titlePrefix?: React.ReactNode;
    titleText?: React.ReactNode;
    titleCount?: React.ReactNode;
}

export interface ISearchResultContainerState {
    sortingState: ISortByCollectionState;
    modalIsOpen: boolean;
    refiners: IProductRefinerHierarchy[] | undefined;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    channelInventoryConfiguration?: ChannelInventoryConfiguration;
    productInventoryStatus?: IProductInventoryInformation[];
    productQuantities: { [key: number]: number };
    working: boolean;
    retrievedActivePrices: boolean;
    showEmptySearchBarTooltip: boolean;
    simpleProducts: SimpleProduct[];
    unitConversions: UnitOfMeasureConversion[];
    breakoffProducts: { [key: number]: boolean };
    shouldShowSubCategories: boolean;
    orderTemplates?: OrderTemplate[];
    cachedCounts: { [key: string]: number };
    searching?: boolean;
    searchingFromOverview?: boolean;
    findEngineHeaterReset?: boolean;
    findEngineHeaterCurrentSearch?: string;
    findEngineHeaterCurrentSearchDescription?: string;
    findEngineSearchParams?: IFindEngineHeaderSearchParams;
    findEngineRegistrationInput?: string;
}

interface ISortByCollectionState {
    selectedSortByOption: ILabeledDropdownOption;
    pending: boolean;
}

type DefaSearchResultContainerProps = IDefaSearchResultContainerProps<IDefaSearchResultContainerData> & {
    context: ICoreContext<IAppSettingsInContext>;
};

/**
 *
 * SearchResultContainer component.
 * @extends {React.PureComponent<IDefaSearchResultContainerProps<IDefaSearchResultContainerData>>}
 */
@observer
export default class SearchResultContainer extends React.PureComponent<DefaSearchResultContainerProps, ISearchResultContainerState> {
    private readonly _viewModeCookieName = 'i365_searchResultViewMode_cookie';

    @computed get isMobile(): boolean {
        return this._viewport === 'xs' || this._viewport === 'sm';
    }

    @computed get shouldShowAvailability(): boolean {
        const {
            config: { disableAvailabilityCheckUnauthorized },
            context: { request: { user: { isAuthenticated } } }
        } = this.props;

        return !disableAvailabilityCheckUnauthorized || isAuthenticated;
    }

    @computed get shouldShowCategoryOverview(): boolean {
        const { data: { category, categories } } = this.props;
        return !!(
            this._pageType === 'Category' &&
            category.result?.ParentCategory === 0 &&
            categories.result &&
            !this.props.data.listPageState.result?.itemIds.length &&
            !this.state.findEngineSearchParams
        );
    }

    private readonly sortByDropdownOptions: ILabeledDropdownOption[] = [
        { key: sortOptions.sortByOptionRelevanceDesc, value: this.props.resources.sortByOptionRelevanceDesc },
        { key: sortOptions.sortByOptionNameAsc, value: this.props.resources.sortByOptionNameAsc },
        { key: sortOptions.sortByOptionNameDesc, value: this.props.resources.sortByOptionNameDesc },
        /* Does not work with customer specific pricing */
        /* { key: sortOptions.sortByOptionPriceAsc, value: this.props.resources.sortByOptionPriceAsc },
        { key: sortOptions.sortByOptionPriceDesc, value: this.props.resources.sortByOptionPriceDesc }, */
        { key: sortOptions.sortByOptionRatingDesc, value: this.props.resources.sortByOptionRatingDesc }
        /* Removed until Microsoft improves this functionality */
        /* { key: sortOptions.sortByOptionBestSelling, value: this.props.resources.sortByOptionBestSelling },
        { key: sortOptions.sortByOptionNewScore, value: this.props.resources.sortByOptionNewScore },
        { key: sortOptions.sortByOptionTrendingScore, value: this.props.resources.sortByOptionTrendingScore } */
    ];

    private readonly _refineItemCommonProps: IRefineItemCommonProps;

    private readonly _pageType: 'Category' | 'Search' | undefined = this.props.config.productScopeAttributeRecordId
        ? 'Category'
        : this.props.context.request.urlTokens.pageType as 'Category' | 'Search' | undefined;

    @observable
    private _viewport: GridSize = 'lg';

    @observable
    private _viewMode: ProductListViewMode | undefined = undefined;

    private readonly _modalToggleRef: React.RefObject<HTMLButtonElement>;

    private readonly _sortAndFilterContainerRef: React.RefObject<LabeledDropdown>;

    private readonly _searchBarInputRef: React.RefObject<HTMLInputElement>;

    // private expandrefinerCount: number | undefined = 0;

    private readonly telemetryContent: ITelemetryContent;

    private readonly productPrices: ProductPrice[];

    private readonly defaultPageNumber = 0;

    private readonly catalogId = getCatalogId(this.props.context.request);

    private isListPageStateUpdated: boolean = false;

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && ArrayExtensions.hasElements(nameTranslations)) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation?.Text;
    }

    public constructor(props: IDefaSearchResultContainerProps<IDefaSearchResultContainerData>, state: ISearchResultContainerState) {
        super(props);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Check required.
        this._viewport = props.context.request?.device?.Type === 'Mobile' ? 'xs' : 'lg';
        this._modalToggleRef = React.createRef<HTMLButtonElement>();
        this._sortAndFilterContainerRef = React.createRef<LabeledDropdown>();
        this._searchBarInputRef = React.createRef<HTMLInputElement>();
        this._toggleModal = this._toggleModal.bind(this);
        this._updateViewport = this._updateViewport.bind(this);
        this.productPrices = [];

        const searchParams = props.data.products.result?.searchParams;
        let engineHeaterStates: Partial<ISearchResultContainerState> = {};

        if (searchParams) {
            if (searchParams.searchType === 'registration') {
                const { Grouped } = groupSearchVehiclesResults({ Vehicles: props.data.products.result?.vehicles?.Vehicles });
                this._groupedVehiclesByEngineHeater = Grouped;
                engineHeaterStates = this._getEngineHeaterSearchResultStateChange(
                    searchParams,
                    { Vehicles: props.data.products.result?.vehicles?.Vehicles },
                    this._groupedVehiclesByEngineHeater
                );
            } else if (searchParams.searchType === 'manual') {
                engineHeaterStates = this._getEngineHeaterSearchResultStateChange(searchParams, {}, []);
            }
        }

        this.state = {
            sortingState: {
                pending: false,
                selectedSortByOption: this.sortByDropdownOptions[0]
            },
            modalIsOpen: false,
            refiners: this.props.data.refiners.result,
            channelInventoryConfiguration: undefined,
            productInventoryStatus: undefined,
            productQuantities: {},
            working: false,
            retrievedActivePrices: false,
            showEmptySearchBarTooltip: false,
            simpleProducts: [],
            unitConversions: [],
            breakoffProducts: {},
            shouldShowSubCategories: !!this.props.data.category.result?.ParentCategory,
            cachedCounts: {},
            findEngineRegistrationInput: searchParams?.registrationNumber,
            ...engineHeaterStates
        };
        const { placeholderTextMax, minLabel, maxLabel, rangeNameFormat } = this.props.resources;
        const locale = this.props.context.request.locale;
        const telemetry = this.props.telemetry;
        const validationErrorNaN = this.props.resources.validationErrorNotNumber;
        const validationErrorRange = this.props.resources.validationErrorNotRange;
        this._refineItemCommonProps = {
            telemetry,
            locale,
            placeholderTextMax,
            minLabel,
            maxLabel,
            rangeNameFormat,
            validationErrorNaN,
            validationErrorRange
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this._updateViewport();

        const savedViewMode = GetCookie(this.props.context.request.cookies, this._viewModeCookieName);
        if (savedViewMode) {
            this._viewMode = +savedViewMode;
        } else if (this.props.config.preferredViewMode) {
            switch (this.props.config.preferredViewMode) {
                case preferredViewMode.listView:
                    this._viewMode = ProductListViewMode.listView;
                    break;
                case preferredViewMode.compactView:
                    this._viewMode = ProductListViewMode.compactView;
                    break;
                case preferredViewMode.cardView:
                default:
                    this._viewMode = ProductListViewMode.cardView;
                    break;
            }
        }
    }
    public async componentDidMount(): Promise<void> {
        const count = this.props.data.products.result?.count;

        if (MsDyn365.isBrowser && window.addEventListener) {
            window.addEventListener('resize', this._updateViewport);
            window.addEventListener('popstate', event => {
                const state = event.state as ListPageState;
                const {
                    context,
                    data: {
                        categories: { result: categories },
                        listPageState: { result: listPageState }
                    }
                } = this.props;

                console.log('popstate', state, listPageState);

                if (!state || !listPageState) {
                    return;
                }

                transaction(() => {
                    Object.assign(listPageState, state);
                    listPageState.navigatingBack = true;

                    if (state.category) {
                        const findCategory = categories?.find(cat => cat.RecordId === state.category)!;
                        context.request.urlTokens.recordId = state.category.toString();
                        this.props.data.category.result = findCategory;
                        this.props.data.categoryHierarchy.result = this._buildCategoryHierarchy(findCategory);
                    }

                    if (state.searchParams) {
                        if (state.searchParams.searchType === 'registration') {
                            this.setState({ findEngineRegistrationInput: state.searchParams.registrationNumber }, () => {
                                void this._onEngineHeaterRegistrationSearch();
                            });
                        } else {
                            this.setState(this._getEngineHeaterSearchResultStateChange(state.searchParams, {}, []));
                        }
                    }
                });
            });
            this._updateViewport();
            window.toggleSortAndFilterModal = this._toggleModal;

            // Only attach onSelectCategory handler if page type is not search
            if (!this.props.context.request.url.requestUrl.searchParams.has('q')) {
                window.onSelectCategory = this._onSelectCategory;
            }
        }

        const listPageState = await this.props.data.listPageState;
        const products = await this.props.data.products;

        if (listPageState.searchText) {
            void GTM.searchEventAsync(listPageState.searchText);
        }

        products.count = count || 0;

        this._updateListPageState(listPageState, products);

        void this._getSimpleProducts();
        void this._getProductsInProductLists();

        if (this.props.context.actionContext.requestContext?.user?.isAuthenticated) {
            if (products.products.some(x => !x.PrefetchedPrice)) {
                void this._getActivePrice();
            } else {
                this.setState({ retrievedActivePrices: true });
            }
        }

        await this._updateVariantAvailability();

        const channelInventoryConfiguration = await StoreOperationsDataActions.getInventoryConfigurationAsync({
            callerContext: this.props.context.actionContext
        });
        this.setState({ channelInventoryConfiguration });

        let productInventoryStatus: IProductInventoryInformation[] | undefined;

        try {
            productInventoryStatus = await this._getAvailableQuantity();
        } catch {
            productInventoryStatus = this.props.data.listPageState.result?.activeProducts.map(p => {
                return {
                    IsProductAvailable: false,
                    StockLevelCode: 'ERR',
                    StockLevelLabel: this.props.resources.availabilityError,
                    ProductAvailableQuantity: {
                        ProductId: p.RecordId
                    }
                };
            });
        }

        this.setState({ productInventoryStatus });

        void when(
            () => this.props.data.categories.status === 'SUCCESS',
            () => {
                const parameterizeFeatureFlags = getParameterizeFeatureFlags(this.props.context.actionContext);

                for (const cat of this.props.data.categories.result || []) {
                    cat.Slug = getCategorySlug(cat, [], parameterizeFeatureFlags);
                    cat.Url = getUrlSync('category', this.props.context.actionContext, { category: cat });
                }
            }
        );

        void this.props.data.orderTemplates.then(orderTemplates => {
            this.setState({ orderTemplates });
        });

        spy((change => {
            return;
            // @ts-expect-error
            if (change.name === 'SearchCriteriaChangedReaction' && change.type === 'action') {
                console.log('Reaction triggered: Search criteria changed', change);
            }
        }));

        // Initialize reaction based on listPageState properties
        reaction(
            () => [
                toJS(listPageState.activeFilters),
                toJS(listPageState.itemIds),
                listPageState.currentPageNumber ?? 0,
                listPageState.searchText ?? '',
                toJS(listPageState.sortingCritera?.Columns),
                listPageState.category ?? 0,
                listPageState.overrideCategory ?? 0,
                this.props.data.category.result?.RecordId ?? 0,
                this.props.data.category.result?.ParentCategory ?? 0
            ],
            this._onSearchCriteriaChanged,
            { equals: comparer.structural, name: 'SearchCriteriaChangedReaction' }
        );

        reaction(
            () => listPageState.activeProducts.map(p => p.RecordId) ?? [],
            async () => {
                let productInventoryStatus: IProductInventoryInformation[] | undefined;

                try {
                    productInventoryStatus = await this._getAvailableQuantity();
                } catch {
                    productInventoryStatus = this.props.data.listPageState.result?.activeProducts.map(p => {
                        return {
                            IsProductAvailable: false,
                            StockLevelCode: 'ERR',
                            StockLevelLabel: this.props.resources.availabilityError,
                            ProductAvailableQuantity: {
                                ProductId: p.RecordId
                            }
                        };
                    });
                }

                this.setState({ productInventoryStatus });
            },
            { equals: comparer.structural }
        );
    }

    public componentWillUnmount(): void {
        window.removeEventListener('resize', this._updateViewport);
    }

    public render(): JSX.Element {
        const {
            config: { imageSettings, className },
            resources,
            data: { listPageState, products: productCollection, featureProduct }
        } = this.props;

        if (!this.isListPageStateUpdated && listPageState.result && productCollection.result) {
            this._updateListPageState(listPageState.result, productCollection.result);
            this.isListPageStateUpdated = true;
        }

        //const products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const searchProduct = listPageState.result?.featureProduct ?? featureProduct.result ?? null;
        let errorText = '';
        // if (!ArrayExtensions.hasElements(products)) {
        if (listPageState.result &&
            listPageState.result.activeProducts.length === 0 &&
            !this.state.searching
        ) {
            errorText = resources.noResultsForRefinersText;

            if (this.state.findEngineSearchParams?.searchType === 'registration') {
                errorText = '';
            }
        }
        const productsComponent = this._getProducts();
        const featureComponent = (
            <FeatureSearchResultItem
                product={searchProduct}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                telemetryContent={this.telemetryContent}
                recommendation={this.props.context.request.query?.recommendation}
            />
        );

        const viewModeProps: IProductListViewModeProps = {
            viewMode: this._viewMode,
            cardButtonTxt: resources.viewModeCardText,
            listButtonTxt: resources.viewModeListText,
            compactButtonTxt: resources.viewModeCompact,
            setCardViewMode: this.setCardViewMode,
            setListViewMode: this.setListViewMode,
            setCompactViewMode: this.setCompactViewMode
        };

        const searchResultContainerViewProps: Partial<ISearchResultContainerViewProps> = {
            ...this.props,
            header: this.props.context.request.url.requestUrl.searchParams.has('q')
                ? (
                    <h2 className="ms-search-result-container__header">
                        {format(resources.searchTextTemplateText || '{0} search results for «{1}»',
                            (listPageState?.result?.totalProductCount ?? this.props.data.products?.result?.count) || 0,
                            listPageState.result?.searchText || this.props.context.request.url.requestUrl.searchParams.get('q') || ''
                        )}
                    </h2>
                )
                : undefined,
            searching: this.state.searching,
            products: productsComponent,
            totalProductCount: this._getTotalProductCount(),
            refineMenu: this._getRefineMenu(),
            className: classnames('ms-search-result-container', className),
            SearchResultContainer: {
                moduleProps: this.props,
                className: classnames('ms-search-result-container', className)
            },
            sortByOptions: this._getSortingDropDown(),
            pagination: this._getPagination(),
            ProductsContainer: { className: 'ms-search-result-container__Products' },
            ProductSectionContainer: { className: 'ms-search-result-container__product-section' },
            RefineAndProductSectionContainer: { className: 'ms-search-result-container__refine-product-section' },
            searchBar: this._getSearchBar(),
            choiceSummary: this._getChoiceSummary(),
            modalToggle: (
                <ModalToggle
                    text={resources.modalTitle}
                    ariaLabel={resources.modalTitle}
                    innerRef={this._modalToggleRef}
                    onClick={this._toggleModal}
                    id='search-result-modal'
                />
            ),
            searchResultModal: this._getSearchResultModal(),
            isMobile: this.isMobile,
            errorMessage: errorText && <ErrorMessage text={errorText} />,
            FeatureSearchContainer: { className: 'ms-feature-search-result' },
            similarLookProduct: searchProduct ? featureComponent : null,
            productPrices: this.productPrices,
            productsDimensionAvailabilities: this.state.productsDimensionAvailabilities,
            viewModesComponent: this.shouldShowCategoryOverview ? undefined : viewModeProps,
            productInventoryStatus: this.state.productInventoryStatus,
            simpleProducts: this.state.simpleProducts,
            unitConversions: this.state.unitConversions,
            onUpdateOrderTemplate: this._onUpdateOrderTemplates,
            retrieveOrderTemplates: this._retrieveOrderTemplates,
            categoryOverview: this._getCategoryOverview(),
            engineHeaterSearch: this._renderEngineHeaterSearch(),
            engineHeaterCurrentSearch: this._renderEngineHeaterCurrentSearch(),
            shouldShowAvailability: this.shouldShowAvailability
        };
        return this.props.renderView(searchResultContainerViewProps as any) as React.ReactElement;
    }

    private readonly _onSearchCriteriaChanged = async (): Promise<void> => {
        this.setState({ searching: true });

        const listPageState = await this.props.data.listPageState;

        // Don't pushState if we're navigating back
        if (!listPageState.navigatingBack) {
            const fullUrl = this.props.data.category.result?.Url
                ? new URL(this.props.data.category.result?.Url, getCurrentUrl(this.props.context.actionContext.requestContext))
                : getCurrentUrl(this.props.context.actionContext.requestContext);
            window.history.pushState(toJS(listPageState), '', buildListPageUrl(fullUrl, listPageState));
        } else {
            listPageState.navigatingBack = false;
        }

        if (listPageState.searchParams?.searchType === 'registration' && listPageState.itemIds.length === 0) {
            this.setState({ searching: false, searchingFromOverview: false, refiners: [] });
            return;
        }

        const categoryId = +(listPageState.overrideCategory || listPageState.category || 0);
        const input = new GetFullProductsByCollectionInput(
            listPageState.pageType,
            this.props.context.request.apiSettings,
            {
                Paging: {
                    Top: this.props.config.itemsPerPage,
                    Skip: Math.max(listPageState.pageSize * (listPageState.currentPageNumber ?? 0), 0)
                },
                count: true,
                Sorting: JSON.parse(JSON.stringify(listPageState.sortingCritera || {}))
            },
            listPageState.activeFilters ?? [],
            categoryId,
            this.props.context.request.query?.productId || this.props.context.request.query?.recommendation
                ? JSON.stringify({
                        ProductId: this.props.context.request.query?.productId,
                        Recommendation: this.props.context.request.query?.recommendation,
                        SearchText: listPageState.searchText
                    })
                : listPageState.searchText,
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Required check.
            !!this.props?.config?.includeAttributes,
            this.props.config.updateRefinerPanel,
            this.props.context.request.locale,
            this.catalogId,
            this.state.channelInventoryConfiguration,
            undefined,
            listPageState.itemIds
        );

        const productResults = await getCollectionProducts(input, this.props.context.actionContext);
        listPageState.totalProductCount = productResults?.count;
        if (this.state.cachedCounts[input.getCacheKey()]) {
            listPageState.totalProductCount = this.state.cachedCounts[input.getCacheKey()];
        }
        listPageState.activeProducts = productResults?.products;

        if (!listPageState.overrideCategory) {
            this.setState((prevState) => ({
                findEngineHeaterReset: !prevState.findEngineHeaterReset,
                findEngineHeaterCurrentSearch: undefined,
                findEngineHeaterCurrentSearchDescription: undefined,
                findEngineSearchParams: undefined
            }));
        }

        const newCachedCounts = { ...this.state.cachedCounts };
        newCachedCounts[input.getCacheKey()] = listPageState.totalProductCount;
        this.setState({ cachedCounts: newCachedCounts });

        void this._getSimpleProducts();
        void this._getProductsInProductLists();
        void this._getActivePrice();

        await this._updateVariantAvailability();

        if (this.props.config.updateRefinerPanel) {
            const refinerInput = new RefinersByCollectionInput(
                listPageState.pageType,
                this.props.context.request.apiSettings,
                {
                    Paging: {
                        Top: this.props.config.itemsPerPage,
                        Skip: Math.max(
                            listPageState.pageSize * (listPageState.currentPageNumber ?? this.defaultPageNumber),
                            this.defaultPageNumber
                        )
                    },
                    count: true,
                    Sorting: JSON.parse(JSON.stringify(listPageState.sortingCritera || {}))
                },
                listPageState.activeFilters ?? [],
                categoryId,
                this.props.context.request.query?.productId || this.props.context.request.query?.recommendation
                    ? JSON.stringify({
                            ProductId: this.props.context.request.query?.productId,
                            Recommendation: this.props.context.request.query?.recommendation,
                            SearchText: listPageState.searchText
                        })
                    : listPageState.searchText,
                false,
                this.props.config.updateRefinerPanel,
                undefined,
                this.catalogId,
                this.state.channelInventoryConfiguration,
                undefined,
                listPageState.itemIds
            );

            const refiners = await getCollectionRefinersAction(refinerInput, this.props.context.actionContext);
            this.setState({ refiners, shouldShowSubCategories: !!this.props.data.category.result?.ParentCategory });
        }

        this.setState({ searching: false, searchingFromOverview: false });
    };

    /**
     * Get products variants dimension availabilities.
     * @returns Void.
     */
    private readonly _updateVariantAvailability = async (): Promise<void> => {
        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = context.app.config.dimensionsInProductCard;
        const dimensionsToDisplayAsSwatch = context.app.config.dimensionsAsSwatchType;
        if (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            ArrayExtensions.hasElements(dimensionsToDisplayAsSwatch) &&
            !dimensionsToDisplayAsSwatch.includes(DimensionTypes.none) &&
            this.props.config.includeAttributes
        ) {
            await this._getProductsDimensionsAvailabilities();
        }
    };

    public readonly setCompactViewMode = () => {
        this._viewMode = ProductListViewMode.compactView;
        this._updateCurrentViewMode();
    };

    public readonly setListViewMode = () => {
        this._viewMode = ProductListViewMode.listView;
        this._updateCurrentViewMode();
    };

    public readonly setCardViewMode = () => {
        this._viewMode = ProductListViewMode.cardView;
        this._updateCurrentViewMode();
    };

    private readonly _updateCurrentViewMode = () => {
        const cookieAge: number = 31536000000;
        const newCookieValue: string = String(this._viewMode);
        SetCookie(this.props.context.request.cookies, newCookieValue, this._viewModeCookieName, cookieAge, true);
    };

    /**
     * Update the list page state with initial data.
     * @param listPageState -- List page State.
     * @param products -- Products.
     */
    @action
    private readonly _updateListPageState = (listPageState: ListPageState, products: IFullProductsSearchResultsWithCount): void => {
        let querySorting: SortColumn[] = [];
        const defaultPageNumber: number = 10;

        if (this._pageType === 'Category') {
            listPageState.pageType = 'Category';
            listPageState.category = this.props.context.request.urlTokens.recordId
                ? parseInt(this.props.context.request.urlTokens.recordId, 10)
                : 0;
        } else {
            listPageState.pageType = 'Search';
        }

        if (this.props.context.request.query?.sorting) {
            querySorting = JSON.parse(decodeURIComponent(this.props.context.request.query.sorting)) as SortColumn[];
        }

        listPageState.currentPageNumber =
            (this.props.context.request.query?.skip &&
                +this.props.context.request.query.skip / (this.props.config.itemsPerPage ?? defaultPageNumber)) ||
            0;
        listPageState.sortingCritera = { Columns: querySorting };
        listPageState.pageSize = this.props.config.itemsPerPage ?? defaultPageNumber;
        listPageState.activeProducts = products.products;
        listPageState.totalProductCount = products.count;
        listPageState.activeFilters = hydrateRefinersFromUrl(this.props.context.request) || [];
        listPageState.searchText = this.props.context.request.query?.q || '';
        if (this.props.context.request.query?.productId) {
            const featureProduct = this.props.data.featureProduct.result;
            if (featureProduct) {
                listPageState.featureProduct = featureProduct;
            }
        }

        if (products.searchParams) {
            const allCategory = this.props.data.categories.result?.find(cat => cat.ParentCategory === 0);

            if (products.searchParams.searchType === 'registration') {
                const { Ids } = groupSearchVehiclesResults({ Vehicles: products.vehicles?.Vehicles });
                listPageState.itemIds = ArrayExtensions.unique(Ids);
            } else if (products.searchParams.searchType === 'manual') {
                const ids = products.vehicles?.Vehicles?.flatMap(r => [ r.ehengheater, r.ehmountkit ].filter(Boolean)) as string[] || [];
                listPageState.itemIds = ArrayExtensions.unique(ids);
            }

            listPageState.overrideCategory = allCategory?.RecordId;
            listPageState.searchParams = products.searchParams;
        }

        if (typeof window !== 'undefined') {
            window.history.replaceState(JSON.parse(JSON.stringify(listPageState)), '');
        }
    };

    /**
     * Get products dimension availabilities.
     * @returns Void.
     */
    private readonly _getProductsDimensionsAvailabilities = async (): Promise<void> => {
        const productsList = this.props.data.listPageState.result?.activeProducts ?? [];

        const productsDimensionAvailabilities = await getDimensionAvailabilitiesForProductSearchResultAction(
            new DimensionAvailabilitiesForProductSearchResultInput(
                productsList,
                this.props.context as ICoreContext<IDimensionsApp>,
                DimensionSwatchDisplayTypes.productCard
            ),
            this.props.context.actionContext
        );

        if (ArrayExtensions.hasElements(productsDimensionAvailabilities)) {
            this.setState({ productsDimensionAvailabilities });
        }
    };

    /**
     * Get the active price for the products.
     */
    private readonly _getActivePrice = async (): Promise<void> => {
        if (!this.props.context.request.user.isAuthenticated) {
            return;
        }

        // Hide prices when retrieving so we don't show base price
        this.setState({ retrievedActivePrices: false });

        const productsList = this.props.data.listPageState.result?.activeProducts ?? [];
        const missingPrices = productsList.filter(x => !x.PrefetchedPrice);

        if (missingPrices.length > 0) {
            try {
                const prices = await getActivePricesAsync(
                    { callerContext: this.props.context.actionContext },
                    {
                        CatalogId: this.catalogId,
                        ChannelId: this.props.context.request.apiSettings.channelId
                    },
                    missingPrices.map(x => x.RecordId),
                    new Date(),
                    null,
                    undefined,
                    true
                );
                const listPageState = await this.props.data.listPageState;

                prices.forEach(p => {
                    const indexOfProduct = listPageState.activeProducts.findIndex(x => x.RecordId === p.ProductId);

                    if (indexOfProduct !== -1) {
                        listPageState.activeProducts[indexOfProduct].Price = p.CustomerContextualPrice || 0;
                        listPageState.activeProducts[indexOfProduct].BasePrice = p.AdjustedPrice || 0;
                        listPageState.activeProducts[indexOfProduct].Badge = getDiscountBadge(p);
                        listPageState.activeProducts[indexOfProduct].ProductPrice = p;
                        listPageState.activeProducts[indexOfProduct].PrefetchedPrice = true;
                    }
                });
            } catch (error) {
                missingPrices.forEach(product => {
                    product.PriceFailed = true;
                });
                console.error('Failed to retrieve active prices.', error);
            }
        }

        this.setState({ retrievedActivePrices: true });
    };

    private readonly _getCategoryOverview = (): React.ReactNode => {
        const { context, data } = this.props;
        const { categories } = data;

        if (!this.shouldShowCategoryOverview) {
            return null;
        }

        const rootCategories = categories.result!.filter(cat => cat.ParentCategory === 0).map(x => x.RecordId);
        const categoriesToShow: CategoryHierarchyData[] = [];

        for (const cat of categories.result!) {
            if (cat.ParentCategory === 0 || !rootCategories.includes(cat.ParentCategory || 0)) {
                continue;
            }

            // Now we know 'cat' is a sub category of a root category
            // Next we add the categories underneath this category but not 'cat' itself, as long as the DisplayOrder is under 9999
            const subCategories = categories.result!.filter(subCat => subCat.ParentCategory === cat.RecordId && (subCat.DisplayOrder || 0) < 9999);
            categoriesToShow.push(...subCategories);
        }

        const parameterizeFeatureFlags = getParameterizeFeatureFlags(this.props.context.actionContext);
        const height = 237;
        const width = 237;
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { w: width, h: height, q: `w=${width}px&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` },
                lg: { w: width, h: height, q: `w=${width}px&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` },
                xl: { w: width, h: height, q: `w=${width}px&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` }
            },
            disableLazyLoad: false,
            lazyload: true,
            quality: 80
        };

        return (
            <div className='ms-search-result-container__category-overview'>
                {categoriesToShow.map(cat => (
                    <div className='ms-search-result-container__category-overview-item' key={cat.RecordId}>
                        <a
                            className='ms-search-result-container__category-overview-item-link'
                            href={getUrlSync('category', context.actionContext, {
                                category: {
                                    ...cat,
                                    Slug: cat.Slug || getCategorySlug({ ...cat }, [], parameterizeFeatureFlags)
                                }
                            }) || '#'}
                            onClick={(e) => {
                                e.preventDefault();
                                this._onSelectCategory(cat, true);
                            }}
                        >
                            <Image
                                src={context.request.apiSettings.baseImageUrl + (cat.Images?.[0]?.Uri || '')}
                                gridSettings={context.request.gridSettings!}
                                imageSettings={defaultImageSettings}
                                loadFailureBehavior='empty'
                                bypassHideOnFailure={true}
                                shouldHideOnFailure={false}
                            />
                            <div className='ms-search-result-container__category-overview-item-name'>
                                {SearchResultContainer.getFriendlyName(context.request.locale, cat.NameTranslations) || cat.Name}
                            </div>
                        </a>
                    </div>
                ))}
            </div>
        );
    };

    private readonly _onEngineHeaterSearch = (searchParams: IFindEngineHeaderSearchParams | undefined, results: ISearchResultProduct[]): void => {
        const ids = results.flatMap(r => [ r.ehengheater, r.ehmountkit ].filter(Boolean)) as string[] || [];

        this._performEngineHeaterSearch(ids, searchParams);

        this.setState(this._getEngineHeaterSearchResultStateChange(searchParams, {}, []));
    };

    @observable private _groupedVehiclesByEngineHeater: IFindEngineHeaterGroupedResults[] = [];

    private readonly _performEngineHeaterSearch = (ids: string[], searchParams: IFindEngineHeaderSearchParams | undefined): void => {
        const { data } = this.props;

        transaction(() => {
            data.listPageState.result!.activeProducts = [];
            data.listPageState.result!.itemIds = ArrayExtensions.unique(ids);
            data.listPageState.result!.searchText = undefined;
            data.listPageState.result!.currentPageNumber = 0;

            const category = data.categories.result?.find(cat => cat.ParentCategory === 0);

            data.listPageState.result!.overrideCategory = category?.RecordId;
            data.listPageState.result!.searchParams = searchParams;
        });
    };

    private readonly _onEngineHeaterRegistrationSearch = async (): Promise<void> => {
        if (!this.state.findEngineRegistrationInput) {
            return;
        }

        const { context } = this.props;
        const results = await searchVehiclesByRegIdAsync({ callerContext: context.actionContext }, this.state.findEngineRegistrationInput);
        const { Grouped, Ids } = groupSearchVehiclesResults(results);

        this._groupedVehiclesByEngineHeater = Grouped;

        let searchParams: IFindEngineHeaderSearchParams | undefined;

        if (Grouped.length > 0) {
            const firstVehicle = results.Vehicles![0];
            const { make, model, specs, modyear } = firstVehicle;
            searchParams = {
                searchType: 'registration',
                type: 'Unknown (shouldn\'t be seen)',
                make: make || 'Unknown',
                model: model || 'Unknown',
                modelSpec: specs,
                engine: undefined,
                year: modyear,
                registrationNumber: this.state.findEngineRegistrationInput
            } as IFindEngineHeaderSearchParams;
        } else {
            searchParams = {
                searchType: 'registration',
                type: 'Unknown (shouldn\'t be seen)',
                make: 'Unknown',
                model: 'Unknown',
                registrationNumber: this.state.findEngineRegistrationInput
            } as IFindEngineHeaderSearchParams;
        }

        this._performEngineHeaterSearch(Ids, searchParams);

        this.setState(this._getEngineHeaterSearchResultStateChange(searchParams, results, this._groupedVehiclesByEngineHeater));
    };

    private readonly _getEngineHeaterSearchResultStateChange = (
        searchParams: IFindEngineHeaderSearchParams | undefined,
        results: ISearchVehiclesResponse,
        groupedResults: IFindEngineHeaterGroupedResults[]
    ): ISearchResultContainerState => {
        let currentSearch: string | undefined;
        let currentSearchDescription: string | undefined;

        if (searchParams?.searchType === 'manual') {
            if (searchParams) {
                const { type, make, model, modelSpec } = searchParams;
                let { engine, year } = searchParams;
                if (engine === 'unknown') {
                    engine = this.props.resources.findEngineHeaterUnknownOptionText;
                }
                if (year === 'unknown') {
                    year = this.props.resources.findEngineHeaterUnknownOptionText;
                }
                const details = [modelSpec, engine, year].filter(Boolean).join(', ');
                currentSearch = `${type}, ${make} ${model}${details ? `, ${details}` : ''}`;
            }
        } else {
            if (groupedResults.length > 0) {
                const firstVehicle = results.Vehicles![0];
                const { make, model, specs, modyear } = firstVehicle;
                currentSearch = groupedResults.length > 1
                    ? `${make} ${model}`
                    : `${make} ${model} ${specs} ${modyear}`;
            } else {
                currentSearch = this.props.resources.findEngineHeaterNoVehicleFoundText;
                currentSearchDescription = this.props.resources.findEngineHeaterNoResultsForRegistrationText;
            }
        }

        const registrationNumberInput = searchParams?.registrationNumber !== undefined
            ? { findEngineRegistrationInput: searchParams.registrationNumber }
            : {};

        return {
            findEngineHeaterCurrentSearch: currentSearch,
            findEngineHeaterCurrentSearchDescription: currentSearchDescription,
            findEngineSearchParams: searchParams,
            ...registrationNumberInput
        } as ISearchResultContainerState;
    };

    private readonly _onEngineHeaterSearchClear = (dontUpdateUrl?: boolean): void => {
        const { data } = this.props;

        transaction(() => {
            data.listPageState.result!.itemIds = [];
            data.listPageState.result!.searchText = undefined;
            data.listPageState.result!.currentPageNumber = 0;
            data.listPageState.result!.overrideCategory = undefined;
            data.listPageState.result!.searchParams = undefined;
        });

        this.setState({
            findEngineRegistrationInput: ''
        });
    };

    private readonly _renderEngineHeaterSearch = (): React.ReactNode => {
        const {
            context,
            data,
            resources
        } = this.props;

        if (data.category.result?.RecordId === undefined) {
            return null;
        }

        if (this.shouldShowCategoryOverview || (!this.state.findEngineSearchParams && data.category.result.RecordId?.toString() !== context.app.config?.engineHeaterCategoryRecordId)) {
            return null;
        }

        let country: string | undefined;
        const baseClassname = 'ms-search-result-container__find-engine-heater';

        if (context.request.url.requestUrl.searchParams.get('country')) {
            // Set country if it's supported
            const countryParam = context.request.url.requestUrl.searchParams.get('country')!.toLowerCase();
            const supportedCountries = ['dk', 'fi', 'no', 'sv'];
            if (supportedCountries.includes(countryParam)) {
                country = countryParam;
            }
        }

        const countryMappingToDEFA = {
            'DK': 'dk',
            'FI': 'fi',
            'NO': 'no',
            'SE': 'sv'
        };
        const supportedCountries = Object.keys(countryMappingToDEFA);
        const countryRegion = data.onlineChannel.result?.CountryRegions?.find(x => supportedCountries.includes(x.IsoCode || ''));

        if (!country) {
            country = countryMappingToDEFA[countryRegion?.IsoCode || ''] || undefined;
        }

        return (
            <div className={`${baseClassname}__container`}>
                {country && (
                    <div className={`${baseClassname}__registration-search__container`}>
                        <div className={`${baseClassname}__registration-search__country`}>
                            <img src={getAsset(`icons/countries/${country}.svg`, context.request)} title={country} />
                            <span>{country.slice(0, 1)}</span>
                        </div>
                        <input
                            className={`${baseClassname}__registration-search__input`}
                            type='text'
                            value={this.state.findEngineRegistrationInput}
                            placeholder={resources.findEngineHeaterSearchByRegistrationPlaceholder}
                            onChange={(e) => this.setState({ findEngineRegistrationInput: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    void this._onEngineHeaterRegistrationSearch();
                                }
                            }}
                        />
                        <button
                            className={`${baseClassname}__registration-search__search-button`}
                            type='button'
                            onClick={this._onEngineHeaterRegistrationSearch}
                        >
                            {resources.searchText}
                        </button>
                    </div>
                )}
                <div className={`${baseClassname}__search__container`}>
                    <LocalDropdown keepChildrenMounted matchWidth={false} placement='bottom-start' trigger={({ ref, onClick, 'aria-expanded': ariaExpanded }) => (
                        <button
                            className={classnames(`${baseClassname}__search__search-button`, { open: ariaExpanded })}
                            ref={ref as any}
                            onClick={onClick}
                            aria-expanded={ariaExpanded}
                        >
                            {resources.findEngineHeaterButtonText}
                        </button>
                    )}
                    >
                        {(close) => (
                            <FindEngineHeater
                                context={context}
                                resources={resources}
                                initialValues={this.props.data.products.result?.searchParams}
                                resetSignal={this.state.findEngineHeaterReset}
                                onClose={close}
                                onSearch={(searchParams, results) => {
                                    this._onEngineHeaterSearch(searchParams, results);
                                    close();
                                }}
                            />
                        )}
                    </LocalDropdown>
                </div>
            </div>
        );
    };

    private readonly _renderEngineHeaterCurrentSearch = (): React.ReactNode => {
        if (!this.state.findEngineHeaterCurrentSearch) {
            return null;
        }

        const { resources } = this.props;

        return (
            <div className='ms-search-result-container__find-engine-heater__current-search'>
                <div style={{ flex: this.state.findEngineHeaterCurrentSearchDescription ? '1' : '0 1 400px' }}>
                    <div className='ms-search-result-container__find-engine-heater__current-search__label'>
                        {resources.findEngineHeaterSearchingForText}
                    </div>
                    <div className='ms-search-result-container__find-engine-heater__current-search__text'>
                        {this.state.findEngineHeaterCurrentSearch}
                    </div>
                    {this.state.findEngineHeaterCurrentSearchDescription && (
                        <div className='ms-search-result-container__find-engine-heater__current-search__description'>
                            {this.state.findEngineHeaterCurrentSearchDescription}
                        </div>
                    )}
                    {(this.state.findEngineSearchParams?.searchType === 'registration' && this._groupedVehiclesByEngineHeater.length > 1) && (
                        <div>
                            <label
                                className='ms-search-result-container__find-engine-heater__current-search__label'
                                htmlFor='specs-dropdown'
                                style={{ display: 'block', marginTop: 8 }}
                            >
                                {resources.findEngineHeaterMultipliResultsForRegistrationText}
                            </label>
                            <select
                                id='specs-dropdown'
                                className='ms-search-result-container__find-engine-heater__current-search__specs-dropdown'
                                disabled={this.state.searching}
                                onChange={(e) => {
                                    const index = parseInt(e.target.value, 10);
                                    const group = this._groupedVehiclesByEngineHeater[index];
                                    const ids = [ group.ehengheater, group.ehmountkit ].filter(Boolean) || [];
                                    this._performEngineHeaterSearch(ids, undefined);
                                }}
                            >
                                {this._groupedVehiclesByEngineHeater.map((group, index) => (
                                    <option key={index} value={index}>
                                        {group.specs.join(' | ')}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
                <button
                    className='ms-search-result-container__find-engine-heater__current-search__clear-button'
                    title={resources.findEngineHeaterClearButtonText}
                    onClick={() => this._onEngineHeaterSearchClear()}
                >
                    {resources.findEngineHeaterClearButtonText}
                </button>
            </div>
        );
    };

    private readonly _getSimpleProducts = async (): Promise<void> => {
        const missing = this.props.data.listPageState.result?.activeProducts?.filter(
            x => !this.state.simpleProducts.find(y => y.RecordId === x.RecordId)
        );

        if (!missing) {
            return;
        }

        const simpleProducts = await getByIdsAsync(
            { callerContext: this.props.context.actionContext },
            this.props.context.request.apiSettings.channelId,
            missing?.map(x => x.RecordId)
        );

        const newSimpleProducts = [...this.state.simpleProducts];

        simpleProducts.forEach(x => {
            if (!newSimpleProducts.find(y => y.RecordId === x.RecordId)) {
                newSimpleProducts.push(x);
            }
        });

        this.setState({ simpleProducts: newSimpleProducts });
    };

    private readonly _getProductsInProductLists = async (): Promise<void> => {
        const { context, data } = this.props;
        const activeProducts = (await data.listPageState).activeProducts;
        const ids = activeProducts.map(x => x.RecordId);

        if (ids.length === 0) {
            return;
        }

        const results = await getProductListsByProductIdsAsync({ callerContext: context.actionContext }, ids);

        if (!results || results.length === 0) {
            return;
        }
        activeProducts.forEach(product => {
            const lists = results.filter(x => x.ProductId === product.RecordId);
            if (lists.length > 0) {
                product.PartOfProductLists = lists;
            }
        });
    };

    private readonly _getSearchResultModal = (): ISearchResultModalViewProps => {
        const {
            resources,
            data: { listPageState, products }
        } = this.props;
        return SearchResultModal({
            resources: {
                modalTitle: resources.modalTitle,
                modalSortAndFilterShowResultsButtonText: resources.modalSortAndFilterShowResultsButtonText
            },
            isOpen: this.state.modalIsOpen,
            returnRef: this._modalToggleRef,
            onModalToggle: this._toggleModal,
            totalProductCount: listPageState?.result?.totalProductCount ?? products?.result?.count
        });
    };

    private readonly _getSortingDropDown = (): ISortByViewProps | undefined => {
        if (this.shouldShowCategoryOverview) {
            return undefined;
        }

        const activeDropdown = this._getCurrentlySelectedOption() || this.state.sortingState.selectedSortByOption;
        let dropdownOptions = this._getAllSortByOptions();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- App config settings are of "any" value.
        if (this.props.context.app.config?.hideRating) {
            dropdownOptions = dropdownOptions.filter(dropdownOption => dropdownOption.key !== sortOptions.sortByOptionRatingDesc);
        }

        const dropdown = (
            <LabeledDropdown
                labelClassname='reviews-list-sort-by'
                dropdownId='categorySortByDropdown'
                dropdownClassname='reviews-list-dropdown'
                toggleColor='link'
                dropdownOptions={dropdownOptions}
                selectedOption={activeDropdown}
                onSelectOption={this._updateSortByDropdown}
                ref={this._sortAndFilterContainerRef}
            />
        );
        return {
            SortingContainer: { className: 'ms-search-result-container__Sort-by-category' },
            sortByDropDown: dropdown
        };
    };

    private readonly _getPagination = (): React.ReactNode => {
        const { config, context, data, resources } = this.props;
        const listPageState = data.listPageState.result;
        const fullUrl = getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || 10;
        // eslint-disable-next-line max-len -- existing code
        const skipCount = !ObjectExtensions.isNullOrUndefined(listPageState?.currentPageNumber)
            ? listPageState!.currentPageNumber * (this.props.config.itemsPerPage || 10)
            : (this.props.context.request.query && +this.props.context.request.query.skip) || 0;
        const totalItems = listPageState?.totalProductCount || 0;
        const previousText = resources.flipperPrevious;
        const nextText = resources.flipperNext;

        if (totalItems <= itemsPerPage) {
            return null;
        }
        return (
            <UncontrolledPagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={resources.paginationAriaLabel}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                telemetryContent={this.telemetryContent}
            />
        );
    };

    private readonly _buildCategoryHierarchy = (n: CategoryHierarchy) => {
        const categories = this.props.data.categories.result;

        if (!categories) {
            return [];
        }

        const categoryHierarchy: CategoryHierarchy[] = [];
        categoryHierarchy.push(n);
        let parentCategory = n.ParentCategory ? categories.find(x => x.RecordId === n.ParentCategory) : undefined;
        while (parentCategory) {
            if (parentCategory.ParentCategory) {
                categoryHierarchy.unshift(parentCategory);
                parentCategory = categories.find(x => x.RecordId === parentCategory!.ParentCategory);
            } else {
                break;
            }
        }

        return categoryHierarchy;
    };

    private readonly _getRefineMenu = (): IRefineMenuViewProps => {
        const { data, context } = this.props;
        const tempRangeTypeTODO = context.request.query && context.request.query.inputRange ? 'input' : 'slider';
        const currentCategory = this.props.data.category.result;
        const validRefiners =
            (this.shouldShowCategoryOverview || this.state.searchingFromOverview)
            ? []
            : this.state.refiners && ArrayExtensions.hasElements(this.state.refiners)
                ? this.state.refiners.filter(refiner => {
                      return ArrayExtensions.hasElements(refiner.Values) && !this._shouldHideFromRefiners(refiner);
                  })
                : data.refiners.result &&
                  data.refiners.result.filter(refiner => {
                      return ArrayExtensions.hasElements(refiner.Values) && !this._shouldHideFromRefiners(refiner);
                  });

        const activeRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        // this._getexpandRefinerCount(validRefiners && validRefiners.length, config.expandRefinersCount);
        // const expandedRefiners = this.expandrefinerCount;
        const subMenus =
            validRefiners &&
            validRefiners.map((productRefinerHierarchy: IProductRefinerHierarchy, index: number) => {
                return (
                    <RefineSubmenu
                        productRefinerHierarchy={productRefinerHierarchy}
                        selectedRefinerValues={activeRefiners}
                        refineItemCommonProps={this._refineItemCommonProps}
                        minValueSliderThumbAriaLabel={this.props.resources.minValueSliderThumbAriaLabel}
                        maxValueSliderThumbAriaLabel={this.props.resources.maxValueSliderThumbAriaLabel}
                        key={index}
                        onUpdateRefiners={this._onUpdateRefiners}
                        urlBuilder={this._buildRefinerUrl}
                        isDisabled={false}
                        isExpandedOnInitialLoad={false}
                        shouldHide={(refiner: IProductRefinerHierarchy, selectedRefinerValues: ProductRefinerValue[]) => {
                            if (refiner.SourceValue !== ProductRefinerSource.Category) {
                                return false;
                            }

                            const selectedValuesOfRefiner = refiner.Values.find(x =>
                                selectedRefinerValues.find(y => y.RefinerRecordId === x.RefinerRecordId)
                            );

                            return !!selectedValuesOfRefiner;
                        }}
                        tempRangeTypeTODO={tempRangeTypeTODO}
                        context={context}
                        moduleId={this.props.id}
                        moduleTypeName={this.props.typeName}
                        telemetryContent={this.telemetryContent}
                        productCountAriaLabel={this.props.resources.productCountAriaLabel}
                        refineItemAriaLabel={this.props.resources.refineItemAriaLabel}
                        refineItemsAriaLabel={this.props.resources.refineItemsAriaLabel}
                        singleProductCountAriaLabel={this.props.resources.singleProductCountAriaLabel}
                        isMobile={this.isMobile}
                        yesText={this.props.resources.yes}
                        noText={this.props.resources.no}
                        noResultsFilterValuesText={this.props.resources.noResultsFilterValuesText}
                    />
                );
            });

        const categories = this.props.data.categories.result;
        let rootCategory = categories?.find(x => !x.ParentCategory);

        if (categories && rootCategory) {
            rootCategory = { ...rootCategory };
            const categoryMap: ICategoryMap = {};
            const locale = this.props.context.request.locale;
            const childCategories = categories.filter(x => x.ParentCategory === rootCategory?.RecordId);

            for (const category of childCategories) {
                const localName =
                    category.NameTranslations?.find(x => x.Language === locale)?.Text ||
                    category.NameTranslations?.[0]?.Text ||
                    category.Name;
                const categoryHierarchy: CategoryHierarchy = { ...category };
                categoryHierarchy.NeutralizedName = category.Name;
                categoryHierarchy.Name = localName || categoryHierarchy.NeutralizedName;
                categoryMap[category.RecordId] = categoryHierarchy;
            }

            categoryMap[rootCategory.RecordId] = rootCategory;

            rootCategory.Url = getCategoryUrl(rootCategory, this.props.context.actionContext, categoryMap);

            for (const category of Object.values(categoryMap)) {
                category.Url = getCategoryUrl(category, this.props.context.actionContext, categoryMap);
            }

            // Code to show only the relevant categories (aka categories that has products with the selected refiners etc.)
            /* const relevantCategories = (this.state.refiners
                ?.find(x => x.SourceValue === 2)
                ?.Values?.map(x => x.RefinerRecordId)
                ?.filter(Boolean) || []) as number[];
            const relevantCategoriesAndParents: number[] = [];

            relevantCategories.forEach(x => {
                relevantCategoriesAndParents.push(x);
                const category = categories.find(y => y.RecordId === x);
                const parents: number[] = [];

                if (category) {
                    let parentCategory = categories.find(y => y.RecordId === category.ParentCategory);
                    if (!parentCategory) {
                        return;
                    }
                    while (parentCategory) {
                        parents.push(parentCategory.RecordId);
                        parentCategory = categories.find(y => y.RecordId === parentCategory!.ParentCategory);
                        if (!parentCategory) {
                            break;
                        }
                    }
                }

                relevantCategoriesAndParents.push(...parents);
            });
            const relevantCategoriesObjects = categories.filter(
                x => relevantCategoriesAndParents && relevantCategoriesAndParents?.includes(x.RecordId)
            ); */

            const currentCategoryFromRefinersRecId = data.listPageState.result?.activeFilters?.find(
                x => x.RefinerSourceValue === ProductRefinerSource.Category
            )?.RefinerRecordId;
            const currentCategoryFromRefiners = categories.find(x => x.RecordId === currentCategoryFromRefinersRecId);

            subMenus?.unshift(
                <RefineCategoryTree
                    label={this.props.resources.refinerCategoryTitle}
                    categories={categories}
                    context={context}
                    currentCategory={this._pageType === 'Category' ? currentCategory : currentCategoryFromRefiners}
                    onSelectCategory={this._onSelectCategory}
                    refiners={this.state.refiners}
                    hideEmptyCategories={this._pageType !== 'Category' && !!this.props.context.request.query?.q}
                />
            );
        }

        return {
            RefineMenuContainer: { className: 'ms-refine-menu' },
            RefinerSectionContainer: { className: 'ms-search-result-container__refiner-section' },
            refiners: subMenus
        };
    };

    /**
     * Should hide from refiners.
     * @param refiner - The refiner.
     * @returns ShouldHideFromRefiners.
     */
    private readonly _shouldHideFromRefiners = (refiner: IProductRefinerHierarchy): boolean => {
        const { channelInventoryConfiguration } = this.state;

        // Hide currency filter
        if (refiner.RecordId === 0 && refiner.DataTypeValue === AttributeDataType.Currency) {
            return true;
        }

        // Hide all category refiners
        if (refiner.RecordId === 0 && refiner.SourceValue === ProductRefinerSource.Category) {
            return true;
        }

        const refinerIds = [
            // @ts-expect-error
            Number(this.props.context.app.config?.prioritySortingAttribute),
            Number(this.props.config.productScopeAttributeRecordId)
        ];

        const agreementProductAttributeRecordIds = getAgreementProductRefinersToHide(
            this.props.context,
            this.props.data.orgInformation.result
        );

        if (agreementProductAttributeRecordIds && agreementProductAttributeRecordIds.length > 0) {
            refinerIds.push(...agreementProductAttributeRecordIds);
        }

        // Always hide sortable attribute since it's just for sorting products in Azure search, not for customers
        return (
            refiner.RecordId === this.props.data.products.result?.inventoryAwareSortableAttributeId ||
            refiner.RecordId === channelInventoryConfiguration?.ProductAvailabilitySortableAttributeRecordId ||
            (refiner.RecordId && refinerIds.includes(refiner.RecordId)) ||
            (this.props.context.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.HideOOS &&
                (refiner.RecordId === this.props.data.products.result?.channelInventoryConfigurationId ||
                    refiner.RecordId === channelInventoryConfiguration?.InventoryProductAttributeRecordId))
        );
    };

    private readonly _canSearchEmpty = (): Boolean => {
        // If we're on a recommendation page, we can search for empty string. Will show entire recommendation list
        if (this.props.context.request.query?.recommendation) {
            return true;
        }
        // If we're on a category page, we can search for empty string. Will show entire category
        if (this._pageType === 'Category') {
            return true;
        }
        return false;
    };

    private readonly _getSearchBar = (): React.ReactNode => {
        const {
            config: { enableSearchBar },
            data: { listPageState },
            resources: { searchPlaceholderText }
        } = this.props;

        if (!enableSearchBar) {
            return null;
        }

        const onKeyDown = async (e: React.KeyboardEvent) => {
            this.setState({ showEmptySearchBarTooltip: false });

            if (e.key === 'Enter') {
                const value = (e.target as HTMLInputElement).value;

                if (!value && !this._canSearchEmpty()) {
                    if (this._pageType !== 'Category') {
                        const rootCategory = this.props.data.categories.result?.find(x => !x.ParentCategory);

                        if (rootCategory) {
                            const rootCategorySlug = getCategorySlug(rootCategory, []);
                            const rootCategoryUrl = getUrlSync('category', this.props.context.actionContext, {
                                category: rootCategory
                            })?.replace('/undefined', `${rootCategorySlug}/`);

                            if (rootCategoryUrl) {
                                window.location.href = rootCategoryUrl;
                                return;
                            }
                        }
                    }

                    // Can't search for empty string
                    this.setState({ showEmptySearchBarTooltip: true });
                    return;
                }

                transaction(() => {
                    listPageState.result!.currentPageNumber = 0;
                    listPageState.result!.searchText = value;
                });

                if (value) {
                    void GTM.searchEventAsync(value);
                }
            }
        };

        return (
            <div className='msc-search-bar__wrapper'>
                <div className='msc-search-bar__container'>
                    <button
                        className='msc-search-bar__button no-style'
                        title={this.props.resources.searchText}
                        onClick={() => {
                            const event = new KeyboardEvent('keydown', {
                                key: 'Enter',
                                code: 'Enter',
                                keyCode: 13,
                                charCode: 13,
                                bubbles: true,
                                cancelable: true
                            } as any);

                            this._searchBarInputRef.current!.dispatchEvent(event);
                        }}
                    />
                    <input
                        ref={this._searchBarInputRef}
                        className='msc-search-bar__input'
                        placeholder={searchPlaceholderText}
                        defaultValue={this.props.context.request.query?.q}
                        onKeyDown={onKeyDown}
                        onBlur={() => this.setState({ showEmptySearchBarTooltip: false })}
                    />
                    <button
                        className='msc-search-bar__clear no-style'
                        title={this.props.resources.clearAllText}
                        onClick={() => {
                            this._searchBarInputRef.current!.value = '';
                            transaction(() => {
                                listPageState.result!.currentPageNumber = 0;
                                listPageState.result!.searchText = '';
                            });
                        }}
                    />
                    <Tooltip
                        isOpen={this.state.showEmptySearchBarTooltip}
                        target='.msc-search-bar__container'
                        className='msc-search-bar__tooltip'
                    >
                        {this.props.resources.searchEmptySearchTermText}
                    </Tooltip>
                </div>
            </div>
        );
    };

    private readonly _getChoiceSummary = (): React.ReactNode => {
        const { resources, data, telemetry } = this.props;
        const { channelInventoryConfiguration } = this.state;
        const selectedRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];
        const refinerValueName = this.state.refiners?.find(rating => rating.KeyName === 'Rating');

        if (this.shouldShowCategoryOverview) {
            return null;
        }

        return (
            <ChoiceSummary
                classNames='ms-choice-summary-by-category'
                clearAllText={resources.clearAllText}
                label={resources.choiceSummaryLabel}
                closeAriaLabel={resources.closeAriaLabel}
                category={data.category.result}
                selectedChoices={selectedRefiners}
                searchText={data.listPageState.result?.searchText}
                searchPrefixText={resources.choiceSummarySearchPrefixText}
                refinerHierarchy={this.state.refiners || []}
                choiceFormat={resources.choiceFormat}
                choiceRangeValueFormat={resources.choiceRangeValueFormat}
                telemetry={telemetry}
                onChoiceClicked={this._onChoiceClicked}
                urlBuilder={this._buildRefinerUrlForChoiceSummary}
                choiceAriaLabel={resources.choiceAriaLabel}
                telemetryContent={this.telemetryContent}
                context={this.props.context}
                channelInventoryConfigurationId={channelInventoryConfiguration?.InventoryProductAttributeRecordId}
                refinerValues={refinerValueName}
                yesLabel={resources.yes}
                noLabel={resources.no}
                noneLabel={resources.noneLabel}
            />
        );
    };
    private readonly _getAvailableQuantity = async (): Promise<IProductInventoryInformation[] | undefined> => {
        const { context } = this.props;
        const products = (await this.props.data.listPageState).activeProducts;

        if (this.shouldShowAvailability === false) {
            return undefined;
        }

        if (!products || products.length === 0) {
            return undefined;
        }

        const productIds = products.map(p => p.RecordId);
        // If set get all warehouses then filter it afterwards
        const inventLocationId = this.props.context.app.config?.forceInventLocationId;
        const shippingSearchCriteria = createInventoryAvailabilitySearchCriteria(
            context.actionContext,
            productIds,
            !inventLocationId,
            inventLocationId ? true : undefined,
            undefined,
            DeliveryMode.shipping
        );
        const shippingAvailability = await ProductsDataActions.getEstimatedAvailabilityAsync(
            { callerContext: context.actionContext },
            shippingSearchCriteria
        );

        if (shippingAvailability.ProductWarehouseInventoryAvailabilities) {
            const shippingInventory = mapAggregatedProductInventoryInformation(context.actionContext, shippingAvailability)
                .filter(x => inventLocationId ? x.InventLocationId === inventLocationId : true);

            if (ArrayExtensions.hasElements(shippingInventory)) {
                shippingInventory.forEach(x => {
                    x.deliveryType = DeliveryMode.shipping;
                    x.ProductAvailableQuantity.ExtensionProperties = shippingAvailability.ProductWarehouseInventoryAvailabilities?.find(
                        y => y.ProductId === x.ProductAvailableQuantity.ProductId
                    )?.ExtensionProperties;
                });
                return shippingInventory;
            } else {
                return undefined;
            }
        } else {
            return undefined;
        }
    };
    /**
     * Get Active products.
     * @returns - JSX Elements.
     */
    private readonly _getProducts = (): JSX.Element => {
        const {
            context,
            config: { imageSettings, allowBackNavigation: shouldAllowBackNavigation },
            data
        } = this.props;
        // const { resources } = this.props;
        const products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const resources = {
            ...this.props.resources,
            buyNowButtonText: this.props.resources.addToCartButtonText,
            addToWishlistText: this.props.resources.addToWishlistButtonText,
            removeButtonText: this.props.resources.removeFromWishlistButtonText
        };
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Reading config value.
        const breadCrumbType = this.props.context.app.config?.breadcrumbType;
        const shouldAllowBack = shouldAllowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');
        const quickviewSlot = this.props.slots?.quickview?.[0];
        const productComparisonButton = ArrayExtensions.hasElements(this.props.slots.productComparisonButton)
            ? this.props.slots.productComparisonButton[0]
            : null;

        const isPriceMinMaxFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );

        if (this._viewMode === ProductListViewMode.cardView) {
            return (
                <ProductSearchResultItems
                    cart={this.props.data.cart.result}
                    products={products}
                    simpleProducts={this.state.simpleProducts}
                    orgInformation={this.props.data.orgInformation.result}
                    customerInformation={this.props.data.customerInformation.result}
                    unitConversions={this.state.unitConversions}
                    productInventoryStatus={this.state.productInventoryStatus || undefined}
                    wishlists={this.props.data.wishlists.result}
                    context={this.props.context}
                    config={this.props.config}
                    imageSettings={imageSettings}
                    resources={resources}
                    moduleType={this.props.typeName}
                    moduleId={this.props.id}
                    allowBackNavigation={shouldAllowBack}
                    disableBuyButtons={!this.state.retrievedActivePrices}
                    showAvailability={this.shouldShowAvailability}
                    showPrices={this.props.data.products.result?.products.every(x => x.PrefetchedPrice) || this.state.retrievedActivePrices}
                    telemetryContent={this.telemetryContent}
                    quickviewSlot={quickviewSlot}
                    productComparisonButton={productComparisonButton}
                    channelInventoryConfigurationId={this.props.data.products.result?.channelInventoryConfigurationId}
                    isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                    productsDimensionAvailabilities={this.state.productsDimensionAvailabilities}
                    productInventoryInformation={this.state.productInventoryStatus}
                    onUpdateOrderTemplate={this._onUpdateOrderTemplates}
                    retrieveOrderTemplates={this._retrieveOrderTemplates}
                />
            );
        }

        const productList = productSearchResults2ProductList(
            products,
            this.props.data.products.result?.channelInventoryConfigurationId,
            this.props.data.cart.result,
            this.state.productInventoryStatus || undefined
        );

        if (this._viewMode === ProductListViewMode.compactView) {
            return (
                <ProductListCompactComponent
                    context={this.props.context}
                    moduleId={this.props.id}
                    moduleType={this.props.typeName}
                    resources={resources}
                    showPrices
                    products={productList}
                    simpleProducts={this.state.simpleProducts}
                    customerInformation={this.props.data.customerInformation.result}
                    cart={this.props.data.cart.result}
                    unitConversions={this.state.unitConversions}
                    wishlists={this.props.data.wishlists.result}
                    imageSettings={imageSettings}
                    showAvailability={this.shouldShowAvailability}
                    onUpdateOrderTemplates={this._onUpdateOrderTemplates}
                    retrieveOrderTemplates={this._retrieveOrderTemplates}
                />
            );
        }

        return (
            <ProductListComponent
                cart={this.props.data.cart.result}
                products={productList}
                simpleProducts={this.state.simpleProducts}
                customerInformation={this.props.data.customerInformation.result}
                unitConversions={this.state.unitConversions}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                selectOnQuantityChange
                productComparisonButton={productComparisonButton}
                actionsAvailable={{
                    addToCartAction: true,
                    addToWishlistAction: true,
                    productComparisonButton: false
                }}
                disableBuyButtons={this.state.working || !this.state.retrievedActivePrices}
                showAvailability={this.shouldShowAvailability}
                showPrices={this.props.data.products.result?.products.every(x => x.PrefetchedPrice) || this.state.retrievedActivePrices}
                showCartCount
                showAgreementProduct={hasAgreementProducts(context, data.orgInformation.result)}
                buyAlwaysAdd
                wishlists={this.props.data.wishlists.result}
                breakoffProducts={this.state.breakoffProducts}
                onBreakoffChange={(product, enabled) => {
                    if (!product.RecordId) {
                        return;
                    }

                    this.setState(prevState => ({
                        breakoffProducts: { ...prevState.breakoffProducts, [product.RecordId!]: enabled }
                    }));
                }}
                onUpdateOrderTemplates={this._onUpdateOrderTemplates}
                onUpdateQuantity={(product, quantity) => {
                    if (!product.RecordId) {
                        return;
                    }

                    const productQuantities = { ...this.state.productQuantities };

                    productQuantities[product.RecordId] = quantity;
                    this.setState({
                        productQuantities
                    });
                }}
                retrieveOrderTemplates={this._retrieveOrderTemplates}
            />
        );
    };

    private readonly _onUpdateOrderTemplates = async (
        product?: ProductSearchResultExt | ProductListProduct,
        newOrderTemplate?: ProductList
    ): Promise<OrderTemplate[]> => {
        const { context, config } = this.props;
        const { orderTemplates } = this.state;
        let newOrderTemplates;

        if (newOrderTemplate) {
            newOrderTemplates = [...(orderTemplates ? orderTemplates : [])];

            // Add new order template if it doesn't exist
            if (newOrderTemplates.every(x => x.productList.Id !== newOrderTemplate.Id)) {
                newOrderTemplates.splice(0, 0, new OrderTemplate(newOrderTemplate, [], 0));
            }

            if (product) {
                const listPageState = await this.props.data.listPageState;
                const newProducts = [...listPageState.activeProducts];
                const newProduct = newProducts?.find(x => x.RecordId === product.RecordId);

                if (newProduct) {
                    const newPartOfProductList: IProductListByProductId = {
                        ProductId: newProduct.RecordId,
                        ProductListId: newOrderTemplate?.Id,
                        Name: newOrderTemplate?.Name
                    };
                    if (!newProduct.PartOfProductLists) {
                        newProduct.PartOfProductLists = [];
                    }
                    newProduct.PartOfProductLists.push(newPartOfProductList);

                    listPageState.activeProducts = newProducts;
                }
            }
        } else {
            // Refresh order templates incase new order template was created
            const queryResultSettings = QueryResultSettingsProxy.fromInputData({
                requestContext: context.request,
                config: config
            }).QueryResultSettings;
            const input = new GetOrderTemplatesInput('', queryResultSettings);
            newOrderTemplates = (await getOrderTemplates(input, context.actionContext)).map(x => new OrderTemplate(x, [], 0));
        }

        this.setState({ orderTemplates: newOrderTemplates });
        return newOrderTemplates;
    };

    private readonly _retrieveOrderTemplates = async (): Promise<OrderTemplate[]> => {
        return this.state.orderTemplates ? this.state.orderTemplates : this._onUpdateOrderTemplates();
    };

    private readonly _getTotalProductCount = (): React.ReactNode => {
        const {
            data: { listPageState, products }
        } = this.props;

        if (this.shouldShowCategoryOverview) {
            return null;
        }

        const totalProductCount = listPageState?.result?.totalProductCount ?? products?.result?.count;

        if (!totalProductCount && this.state.searching) {
            return null;
        }

        return (
            <div className={`ms-search-result-container__total-product-count`}>
                {format(this.props.resources.numberOfSearchResults, totalProductCount || 0)}
            </div>
        );
    };

    private readonly _updateSortByDropdown = (notification: ILabeledDropdownOnChangeNotification): void => {
        const {
            context: { request: requestContext, actionContext }
        } = this.props;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            // eslint-disable-next-line max-len -- existing code
            const error = `[sort-by-category] cannot sort without context: ${!requestContext ? 'requestContext ' : ''} ${
                !actionContext ? 'actionContext ' : ''
            } could not be found`;
            this.props.telemetry.warning(error);
            return;
        }

        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.sortingCritera = {
                Columns: [this._getSortColumnFromSelectedOption(notification.selectedOption)]
            };
        });
    };

    private readonly _getSortColumnFromSelectedOption = (option: ILabeledDropdownOption): SortColumn => {
        const { data } = this.props;

        if (!data.searchConfiguration.result) {
            return {};
        }

        const mappedConfiguration = data.searchConfiguration.result.find(searchConfiguration => {
            return searchConfiguration.key === option.key;
        });

        if (mappedConfiguration) {
            return mappedConfiguration.sortColumn;
        }

        return {};
    };

    /**
     * Retrieves current sort criteria.
     * @returns Sort criteria column which is currently active or undefined if it's not specified.
     */
    private readonly _getSortCriteriaColumn = (): SortColumn | undefined => {
        const sortCriteria = this.props.data.listPageState.result?.sortingCritera;
        if (ArrayExtensions.hasElements(sortCriteria?.Columns)) {
            return sortCriteria!.Columns[0];
        }

        const queryColumns = parseQueryParam<SortColumn[]>('sorting', this.props.context.request);
        if (ArrayExtensions.hasElements(queryColumns)) {
            return queryColumns[0];
        }

        return undefined;
    };

    /**
     * Retrieves dropdown options.
     * @returns All dropdown options.
     */
    private readonly _getAllSortByOptions = (): ILabeledDropdownOption[] => {
        const searchConfiguration = this.props.data.searchConfiguration.result;

        if (!searchConfiguration) {
            return [];
        }

        const mappedSearchConfiguration = this.sortByDropdownOptions.filter(mappedSearchConfig =>
            searchConfiguration.find(item => mappedSearchConfig.key === item.key)
        );

        return ArrayExtensions.validValues(mappedSearchConfiguration);
    };

    /**
     * Retrieves current selected dropdown option based on the sort criteria.
     * @returns Current dropdown option.
     */
    private readonly _getCurrentlySelectedOption = (): ILabeledDropdownOption | undefined => {
        const activeSortColumn = this._getSortCriteriaColumn();

        const searchConfiguration = this.props.data.searchConfiguration.result;

        if (!activeSortColumn || !searchConfiguration) {
            return undefined;
        }

        const activeMappedConfig = searchConfiguration.find(
            mappedSearchConfig =>
                mappedSearchConfig.sortColumn.ColumnName === activeSortColumn.ColumnName &&
                mappedSearchConfig.sortColumn.IsDescending === activeSortColumn.IsDescending
        );
        if (!activeMappedConfig) {
            return undefined;
        }

        return this.sortByDropdownOptions.find(dropdownOption => dropdownOption.key === activeMappedConfig.key);
    };

    private readonly _onUpdateRefiners = (itemToggleNotification: IRefineItemToggleNotification): void => {
        const {
            context: { request: requestContext, actionContext }
        } = this.props;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            const error = `Refine menu cannot refine search criteria: 
                ${!requestContext ? 'requestContext ' : ''} ${!actionContext ? 'actionContext ' : ''} could not be found`;
            this.props.telemetry.warning(error);
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Server side the list PageState will not be called.
        const updatedRefinementCriteria = getUpdatedRefinementCriteria(
            itemToggleNotification,
            this.props.data.listPageState.result?.activeFilters || []
        );

        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.activeFilters = updatedRefinementCriteria;
        });
    };

    private readonly _buildRefinerUrl = (itemToggleNotification: IRefineItemToggleNotification): string => {
        if (this.props.data.listPageState.result) {
            const newRefinementCriteria = getUpdatedRefinementCriteria(
                itemToggleNotification,
                this.props.data.listPageState.result.activeFilters || []
            );

            const tempListPageState = { ...this.props.data.listPageState.result, activeFilters: newRefinementCriteria };

            return buildListPageUrl(getCurrentUrl(this.props.context.request), tempListPageState);
        }
        this.props.telemetry.warning('[buildRefinerQueryString]List Page State Not available, unable to build refiner URL');
        return '';
    };

    private readonly _onChoiceClicked = (notification: IChoiceSummaryClickNotification): void => {
        const { data, telemetry } = this.props;

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._onChoiceClicked]ListPageState unavailable, unable to update refiners.');
            return;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        // We need to navigate to all category if clearing search term or clearing all filters while not being on category page
        if (this._pageType !== 'Category' && (notification.clearAll || notification.clearSearchText)) {
            const currentCategoryRecordId = currentRefinementCriteria.find(x => x.RefinerSourceValue === ProductRefinerSource.Category)?.RefinerRecordId;
            const currentCategory = data.categories.result?.find(x => x.RecordId === currentCategoryRecordId);
            const rootCategory = data.categories.result?.find(x => !x.ParentCategory);

            // Navigate to current category page or root if current category can't be resolved
            if (currentCategory?.Url || rootCategory?.Url) {
                window.location.href = currentCategory?.Url || rootCategory?.Url || '';
            }
            return;
        }

        if (notification.clearAll) {
            // Set focus to sort and filter
            const dropdownElementId = this._sortAndFilterContainerRef.current && this._sortAndFilterContainerRef.current.props.dropdownId;
            const dropdownElement = dropdownElementId && document.getElementById(dropdownElementId);
            setTimeout(() => {
                dropdownElement && dropdownElement.focus();
            }, 50);

            this._clearCategory(true);
        } else if (notification.clearCategory) {
            this._clearCategory();
        } else if (notification.clearSearchText) {
            transaction(() => {
                data.listPageState.result!.currentPageNumber = 0;
                data.listPageState.result!.searchText = '';
            });
        } else {
            const selectedChoice = notification.choiceClicked;
            if (!selectedChoice) {
                telemetry.warning('[choice-summary-by-category._onChoiceClicked] Choice could not be determined');
                return;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedChoice, selectedCriterion)
            );

            transaction(() => {
                this.props.data.listPageState.result!.currentPageNumber = 0;
                this.props.data.listPageState.result!.activeFilters = currentRefinementCriteria;
            });
        }
    };

    private readonly _onSelectCategory = (categoryOrRecId: CategoryHierarchyData | number, selected: boolean): void => {
        const { context, data, telemetry } = this.props;

        this.setState({ searching: true, searchingFromOverview: data.category.result?.ParentCategory === 0 });

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._onChoiceClicked]ListPageState unavailable, unable to update refiners.');
            return;
        }

        const category = typeof categoryOrRecId === 'number'
            ? data.categories.result?.find(cat => cat.RecordId === categoryOrRecId)!
            : categoryOrRecId;

        if (this._pageType === 'Category' && selected) {
            transaction(() => {
                context.request.urlTokens.recordId = category.RecordId.toString();

                const parameterizeFeatureFlags = getParameterizeFeatureFlags(context.actionContext);
                category.Slug = getCategorySlug(category, [], parameterizeFeatureFlags);
                category.Url = getUrlSync('category', context.actionContext, { category });

                data.category.result = category;
                data.categoryHierarchy.result = this._buildCategoryHierarchy(category);

                const listPageState = data.listPageState.result!;
                listPageState.activeFilters = [];
                listPageState.category = category.RecordId;
                listPageState.currentPageNumber = 0;
                listPageState.itemIds = [];
                listPageState.searchParams = undefined;
                listPageState.overrideCategory = undefined;
            });
        } else if (selected) {
            let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];
            const selectedCategoryRefiner: ProductRefinerValue = {
                DataTypeValue: AttributeDataType.Text,
                LeftValueBoundLocalizedString: '',
                LeftValueBoundString: SearchResultContainer.getFriendlyName(context.request.locale, category.NameTranslations) || category.Name,
                RefinerRecordId: category.RecordId,
                RefinerSourceValue: ProductRefinerSource.Category,
                RightValueBoundLocalizedString: '',
                RightValueBoundString: SearchResultContainer.getFriendlyName(context.request.locale, category.NameTranslations) || category.Name
            };

            currentRefinementCriteria = currentRefinementCriteria.filter(x => x.RefinerSourceValue !== ProductRefinerSource.Category);
            currentRefinementCriteria.push(selectedCategoryRefiner);

            transaction(() => {
                this.props.data.listPageState.result!.currentPageNumber = 0;
                this.props.data.listPageState.result!.activeFilters = currentRefinementCriteria;
            });
        } else {
            this._clearCategory();
        }
    };

    private readonly _buildRefinerUrlForChoiceSummary = (selectedRefiner: ProductRefinerValue, isClearAll: boolean): string => {
        const { data, telemetry } = this.props;
        const fullUrl = getCurrentUrl(this.props.context.request);

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._buildRefinerUrl]ListPageState unavailable, unable to create refiner URL.');
            return fullUrl.href;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        if (!isClearAll) {
            if (!selectedRefiner) {
                telemetry.warning('[choice-summary-by-category._buildRefinerUrl] URL for Choice could not be determined');
                return fullUrl.href;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedRefiner, selectedCriterion)
            );
        } else {
            currentRefinementCriteria = [];
        }

        const tempListPageState = { ...data.listPageState.result, activeFilters: currentRefinementCriteria };

        return buildListPageUrl(fullUrl, tempListPageState);
    };

    private _getViewport(): GridSize {
        if (MsDyn365.isBrowser) {
            return isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        }
        return isMobile({ variant: VariantType.Viewport, context: this.props.context.request });
    }

    private _toggleModal(): boolean {
        if (this.shouldShowCategoryOverview) {
            return false;
        }

        this.setState({
            modalIsOpen: !this.state.modalIsOpen
        });

        return true;
    }

    private _updateViewport(): void {
        this._viewport = this._getViewport();

        if (this.state.modalIsOpen && !this.isMobile) {
            this._toggleModal();
        }
    }

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className='ms-search-result__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-search-result__pagination-right' aria-hidden='true' />
            </div>
        );
    };

    private readonly _getRootProductCategory = (category: CategoryHierarchy): CategoryHierarchy | undefined => {
        if (category.ParentCategory) {
            const parent = this.props.data.categories.result?.find(x => x.RecordId === category.ParentCategory)!;
            if (!parent.ParentCategory) {
                return category;
            }
            return this._getRootProductCategory(parent);
        } else {
            return category;
        }
    };

    private readonly _clearCategory = (clearFilters?: boolean): void => {
        if (this._pageType !== 'Category') {
            const newActiveFilters = clearFilters
                ? []
                : this.props.data.listPageState.result?.activeFilters?.filter(x => x.RefinerSourceValue !== ProductRefinerSource.Category) || [];
            transaction(() => {
                this.props.data.listPageState.result!.currentPageNumber = 0;
                this.props.data.listPageState.result!.activeFilters = newActiveFilters;
            });
            return;
        }

        transaction(() => {
            // If clearing a root product category (not all products root category), go to root category (all products)
            // If clearing a sub category, go to nearest root product category
            // If clearing all filters, go to root category (all products)

            // Old method for going up to root category before going to all.
            /* const currentCategory = this.props.data.category.result;
            const parentCategory =
                currentCategory?.ParentCategory && this.props.data.categories.result?.find(x => x.RecordId === currentCategory.ParentCategory);
            let newCategory =
                clearFilters || (parentCategory && !parentCategory.ParentCategory)
                    ? this.props.data.categories.result?.find(x => !x.ParentCategory)
                    : this._getRootProductCategory(currentCategory!); */
            let newCategory = this.props.data.categories.result?.find(x => !x.ParentCategory);

            if (newCategory) {
                newCategory = { ...newCategory };
            } else {
                return;
            }

            // Since only one category can be selected at a time, deselecting a category should go back to the root category
            this.props.context.request.urlTokens.recordId = newCategory.RecordId.toString();
            this.props.data.category.result = newCategory;
            this.props.data.categoryHierarchy.result = this._buildCategoryHierarchy(newCategory);

            // Root category wont have URL so we need to build it
            const parameterizeFeatureFlags = getParameterizeFeatureFlags(this.props.context.actionContext);
            newCategory.Slug = getCategorySlug(newCategory, [], parameterizeFeatureFlags);
            newCategory.Url = getUrlSync('category', this.props.context.actionContext, { category: newCategory });

            // Deselecting means going back to root category which will contain any non-category refiners
            const newActiveFilters = clearFilters
                ? []
                : this.props.data.listPageState.result?.activeFilters?.filter(x => x.RefinerSourceValue !== ProductRefinerSource.Category) ||
                [];
                this.props.data.listPageState.result!.category = newCategory.RecordId;
                this.props.data.listPageState.result!.currentPageNumber = 0;
                this.props.data.listPageState.result!.activeFilters = newActiveFilters;
                this.props.data.listPageState.result!.overrideCategory = undefined;
                this.props.data.listPageState.result!.searchParams = undefined;
        });
    };
}
